// vendor modules
import React, { Component } from "react"
import PropTypes from "prop-types"
import { List, Map } from "immutable"
import axios from "axios"

// internal modules
import {
    TASTYPIE_QUERY_LIMIT,
    NO_RESULTS_ERROR,
    NO_RESULTS_EXPLANATION,
    NO_RESULTS_DEFAULT_FILTERS_EXPLANATION,
    NO_RESULTS_NO_FILTERS_EXPLANATION,
} from "shared/imports/sharedConstants"

import isFeatureEnabled from "shared/featureflags/helperFunctions"
import paths from "app/static/frontend/imports/desktopPaths"
import PlatformSpecificListView from "app/static/frontend/search-new/containers/ListView"
import debounce from "lodash.debounce"

export default class ListView extends Component {
    static propTypes = {
        // from containers/listview/index (objects)
        currentPage: PropTypes.number.isRequired,
        currentCount: PropTypes.number,
        currentData: PropTypes.object,
        isLoading: PropTypes.bool.isRequired,
        isLoadingMore: PropTypes.bool.isRequired,
        loadingDataError: PropTypes.object,
        allDefaultFiltersActive: PropTypes.bool.isRequired,
        nonDefaultFiltersActive: PropTypes.bool.isRequired,
        currentFilters: PropTypes.object,
        searchifyQuerysets: PropTypes.object,
        hasNewFiltersOnInitialLoad: PropTypes.bool.isRequired,
        lastLoadedUserUpdate: PropTypes.string,
        // from containers/listview/index (action creators)
        fetchSearchData: PropTypes.func.isRequired,
        registerPermanentFilters: PropTypes.func.isRequired,
        resetFilters: PropTypes.func.isRequired,
        storeLastLoadedUserUpdate: PropTypes.func.isRequired,
        // from filterablelistviews.js
        inlineComponent: PropTypes.func.isRequired,
        resource: PropTypes.object.isRequired,
        uniqueFlvId: PropTypes.string.isRequired,
        getRefreshablePermanentFilters: PropTypes.func,
        // searchify
        isBulkActions: PropTypes.bool,
        isSearchify: PropTypes.bool,
        // api-caching
        resourceUpdatedTimestamp: PropTypes.string,
    }

    static defaultProps = {
        currentData: List(),
        currentFilters: Map(),
        searchifyQuerysets: List(),
        isBulkActions: false,
        isSearchify: false,
    }

    constructor(props) {
        super(props)
        this.debouncedLoadMoreData = debounce(this.loadMoreData.bind(this), 150, false)
        this.debouncedRefreshData = debounce(this.refreshData.bind(this), 150, false)

        this.resetFilters = this.resetFilters.bind(this)
        this.state = { refreshing: false }
    }

    componentDidMount() {
        // if the timestamp of the last time the resource was updated does not
        // match the FLV's "last stored update", then we need to refresh the page.
        // The last loaded user update notes the value of "resourceUpdatedTimestamp"
        // at time of loading data. "resourceUpdatedTimestamp" gets changed upon
        // an cache-clearing action such as PATCH or POST. Thus, if they're not the same,
        // the user must have fired PATCH or POST, and we should refetch the data.
        // See api-caching/action-creators for more about cache invalidation.
        const userUpdatedData = this.props.lastLoadedUserUpdate !== this.props.resourceUpdatedTimestamp

        // WE CANNOT ALWAYS CALL "fetchCountAndData" AND RELY ON CACHING. THIS IS DUE
        // TO NEEDING TO RESET THE USER'S SCROLL POSITION IF THEY CLICK BACK.
        //
        // fetchData will call LOADING_DATA_START, which will trigger a loading spinner.
        // This is fine and dandy at all until we want to apply the user's previous
        // list view position in the web app -- that is, if a user scrolls down,
        // clicks an item, goes to the profile, and then goes back, their scrollbar
        // should be in the same place as before. The rendered loading spinner will
        // entirely destroy any previous position. Hence we should only fetch data
        // if we have new filters or new user updated data (stances, new contacts, etc.)
        if (this.props.hasNewFiltersOnInitialLoad || userUpdatedData) {
            this.fetchCountAndData(false)
        }
    }

    componentDidUpdate(oldProps) {
        const filtersChanged = !oldProps.currentFilters.equals(this.props.currentFilters)
        const searchifyQuerysetsChanged = this.props.isSearchify
            ? !oldProps.searchifyQuerysets.equals(this.props.searchifyQuerysets)
            : false

        if (filtersChanged || searchifyQuerysetsChanged) {
            this.cancelRequests()
            this.fetchCountAndData()
        }
    }

    componentWillUnmount() {
        this.cancelRequests()
    }

    fetchCountAndData(shouldLoadFromCache = true) {
        this.countRequest = this.fetchCount(shouldLoadFromCache)
        this.request = this.fetchData(shouldLoadFromCache)

        // store the last object the user updated ***that has gotten reflected
        // in the list view results***. The list view will not automatically
        // refresh if you set a stance on a Bill inline, but it should refresh if
        // you go from Bill search => set an inline stance => Bill profile => back to Bill search.
        // Update it only after the data has actually loaded.
        // Returning to make jest happy.
        return Promise.all([this.countRequest, this.request])
            .then((resp) =>
                this.props.storeLastLoadedUserUpdate(this.props.uniqueFlvId, this.props.resourceUpdatedTimestamp),
            )
            .catch((error) => {
                if (axios.isCancel(error)) {
                    return
                }
                console.error(error)
            })
    }

    fetchCount(shouldLoadFromCache = true) {
        return this.props.fetchSearchData({
            uniqueFlvId: this.props.uniqueFlvId,
            resource: this.props.resource,
            countOnly: true,
            shouldLoadFromCache,
        })
    }

    fetchData(shouldLoadFromCache = true) {
        return this.props.fetchSearchData({
            uniqueFlvId: this.props.uniqueFlvId,
            resource: this.props.resource,
            shouldLoadFromCache,
        })
    }

    cancelRequests() {
        if (this.request) {
            this.request.abort()
            delete this.request
        }

        if (this.countRequest) {
            this.countRequest.abort()
            delete this.countRequest
        }

        if (this.loadMoreRequest) {
            this.loadMoreRequest.abort()
            delete this.loadMoreRequest
        }
    }

    hasMoreDataToLoad() {
        // we're doing this paginating thing because sometimes the count
        // is off (it's only an estimated count), so we can't stop immediately
        // when data length equals the currentCount.
        const nextOffset = (this.props.currentPage + 1) * TASTYPIE_QUERY_LIMIT
        return nextOffset < this.props.currentCount
    }

    loadMoreData() {
        if (this.hasMoreDataToLoad() && !this.props.isLoadingMore) {
            this.loadMoreRequest = this.props.fetchSearchData({
                resource: this.props.resource,
                uniqueFlvId: this.props.uniqueFlvId,
                loadMore: true,
            })
        }
    }

    refreshData() {
        this.setState({ refreshing: true })
        if (this.props.getRefreshablePermanentFilters) {
            this.props.registerPermanentFilters(this.props.uniqueFlvId, this.props.getRefreshablePermanentFilters())
        }

        this.countRequest = this.fetchCount(false)
        this.request = this.fetchData(false)
        this.request.then((response) => this.setState({ refreshing: false }))
    }

    shouldShowSpinner() {
        return this.props.isLoading && !this.state.refreshing
    }

    resetFilters() {
        return this.props.resetFilters(this.props.uniqueFlvId)
    }

    getNoResultsDetails() {
        // there are four possibilities for no data:
        // 1. there's no data because an error occurred during the search
        // 2. there are filters active that are impossible (e.g. senators on House Ways and Means)
        // 3. there are default filters active and somebody sucks at setting default filters
        // 4. There's no data. This is the customizable one!
        if (this.props.currentData.size) {
            return
        }

        let text
        let button1
        let button2

        if (this.props.loadingDataError) {
            // scenario 1
            text = NO_RESULTS_ERROR
        } else if (this.props.nonDefaultFiltersActive) {
            // scenario 2
            text = NO_RESULTS_EXPLANATION
            button1 = {
                text: "Clear Filters",
                action: this.resetFilters,
            }
            if (
                isFeatureEnabled("ff_custom_official", "ff_custom_official_v2") &&
                Userdata.admin_can_create_custom_official &&
                this.props.inlineComponent.name === "OfficialInline"
            ) {
                button2 = {
                    text: "Add custom official",
                    link: paths.editCustomOfficialPrefix,
                    action: () => {
                        this.props.cancelSearchify()
                    },
                }
            }
        } else if (this.props.allDefaultFiltersActive) {
            // scenario 3
            text = NO_RESULTS_DEFAULT_FILTERS_EXPLANATION
            button1 = {
                text: "Clear Default Filters",
                action: this.resetFilters,
            }
            button2 = {
                text: "Manage My Default Filters",
                link: paths.myAccount,
            }
        } else {
            // scenario 4
            text = (this.props.noResultDetails && this.props.noResultDetails.text) || NO_RESULTS_NO_FILTERS_EXPLANATION

            button1 = this.props.noResultDetails && this.props.noResultDetails.button1
            button2 = this.props.noResultDetails && this.props.noResultDetails.button2
        }

        return {
            text,
            buttons: [button1, button2].filter((x) => !!x),
        }
    }

    render() {
        return (
            <PlatformSpecificListView
                {...(this.props.orientation && { key: this.props.orientation })}
                loadMoreData={this.debouncedLoadMoreData}
                refreshData={this.debouncedRefreshData}
                isRefreshing={this.state.refreshing}
                reachedEndOfData={!this.hasMoreDataToLoad()}
                shouldShowSpinner={this.shouldShowSpinner()}
                hasNoResults={this.props.currentData.size === 0}
                noResultsDetails={this.getNoResultsDetails()}
                // pass in ability to refresh data!
                {...this.props}
            />
        )
    }
}
